import { useCallback, useEffect, useState } from "react";
import { Location } from "../../types/location";
import { locationService } from "../../services/locationService";
import PageBreadcrumb from "../../shared/PageBreadCrumb";
import LocationTable from "./list/LocationTableNew";
import RightDrawer from "../../shared/components/form/RightDrawer";
import LocationForm from "./form/LocationForm";

import useDebounce from "../../hooks/useDebounce";
import ErrorAlert from "../../shared/components/ui/ErrorAlert";

const ITEMS_PER_PAGE = 6;
const DEBOUNCE_DELAY = 500;

export default function LocationList() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<Location | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [status, setStatus] = useState<string | null>("all");
  const [page, setPage] = useState<number>(1);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);

  const fetchLocations = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Only send status when explicitly 'active' or 'inactive'. Omit for 'all' so backend returns both.
      const statusParam =
        status && status !== 'all' && status !== '' ? status : undefined;

      const filters = {
        page,
        limit: ITEMS_PER_PAGE,
        sortBy: sortField,
        sortOrder,
        search: debouncedSearchTerm,
        status: statusParam,
      };

      const res = await locationService.getLocations(filters);
      
      setLocations([...(res.data || [])]);
      setTotalItems(res.meta?.totalDocs || 0);
    } catch (err: any) {
      if (err.name === 'CanceledError') {
        console.log('Request was canceled');
        return;
      }

      setError(err.response?.data?.message || "Failed to fetch locations");
      console.error("API Error:", err);
      setLocations([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearchTerm, status, page, sortField, sortOrder]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleAdd = (_item: Location) => {
    setEditingItem(null);
    setOpenForm(false);

    if (page !== 1) {
      setPage(1);
      return;
    }

    fetchLocations();
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRefresh = () => {
    fetchLocations();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6 relative">
      <PageBreadcrumb pageTitle="Location Master" />
      
      {error && (
        <ErrorAlert 
          message={error} 
          onClose={() => setError(null)} 
          className="mb-4"
        />
      )}
      
      <LocationTable
        locations={locations}
        search={searchTerm}
        setSearch={setSearchTerm}
        status={status}
        setStatus={setStatus}
        page={page}
        setPage={handlePageChange}
        totalLocations={totalItems}
        onEdit={(item) => {
          setEditingItem(item);
          setOpenForm(true);
        }}
        onAdd={() => {
          setEditingItem(null);
          setOpenForm(true);
        }}
        onRefresh={handleRefresh}
        sortField={sortField}
        sortOrder={sortOrder}
        setSortField={setSortField}
        setSortOrder={setSortOrder}
        loading={isLoading}
      />
      
      <RightDrawer 
        open={openForm} 
        title={editingItem ? "Edit Location" : "Add Location"}
        onClose={() => {
          setOpenForm(false);
          setEditingItem(null);
        }}
        size="50"

      >
        <LocationForm 
          editingItem={editingItem} 
          onAdd={handleAdd} 
          onClose={() => {
            setOpenForm(false);
            setEditingItem(null);
          }} 
        />
      </RightDrawer>
    </div>
  );
}
